import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../context/AuthContext';
import { TrackingMode } from '../../../types';
import { RosterModule } from './RosterModule';
import { DropInModule } from './DropInModule';

interface AttendanceModuleProps {
  sessionId: string;
  engagementId: string;
  trackingMode: TrackingMode;
  roster: any[];
  attendance: any[];
  headcount: number;
}

export const AttendanceModule: React.FC<AttendanceModuleProps> = ({ 
  sessionId, 
  engagementId, 
  trackingMode, 
  roster, 
  attendance, 
  headcount 
}) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const markAttendanceMutation = useMutation({
    mutationFn: async ({ personId, status }: { personId: string, status: string }) => {
      // Use unique constraint for atomic upsert
      const { error } = await supabase.from('attendance').upsert({
        session_id: sessionId, 
        person_id: personId, 
        status, 
        created_by_email: profile?.email
      }, { onConflict: 'session_id,person_id' });
      
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
  });

  const deleteAttendanceMutation = useMutation({
    mutationFn: async (personId: string) => {
      const { error } = await supabase
        .from('attendance')
        .delete()
        .eq('session_id', sessionId)
        .eq('person_id', personId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
    },
    onError: (err: any) => {
      console.error("Attendance Deletion Failed:", err);
      alert(`System Error: Could not remove attendance record. ${err.message}`);
    }
  });

  const markAllPresentMutation = useMutation({
    mutationFn: async () => {
      if (roster.length === 0) return;
      
      // HIGH PERFORMANCE: Bulk Atomic Upsert
      const upserts = roster.map(p => ({
        session_id: sessionId,
        person_id: p.person_id,
        status: 'present',
        created_by_email: profile?.email
      }));

      const { error } = await supabase.from('attendance').upsert(upserts, { 
        onConflict: 'session_id,person_id' 
      });

      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
  });

  const dropInMutation = useMutation({
    mutationFn: async (person: any) => {
      const isEnrolled = roster.some(p => p.person_id === person.id);
      
      // Step 1: Ensure Participation (Enrollment) using Atomic Upsert
      if (!isEnrolled) {
        const { error: pErr } = await supabase.from('participation').upsert({
          engagement_id: engagementId,
          person_id: person.id,
          status: 'active',
          created_by_email: profile?.email
        }, { onConflict: 'engagement_id,person_id' });
        
        if (pErr) throw pErr;
      }
      
      // Step 2: Ensure Attendance Log using Atomic Upsert
      const { error: aErr } = await supabase.from('attendance').upsert({
        session_id: sessionId,
        person_id: person.id,
        status: 'present',
        created_by_email: profile?.email
      }, { onConflict: 'session_id,person_id' });
      
      if (aErr) throw aErr;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session', sessionId] })
  });

  if (trackingMode === 'headcount') return null;

  if (trackingMode === 'drop_in') {
    return (
      <DropInModule 
        sessionId={sessionId}
        engagementId={engagementId}
        attendance={attendance}
        onDropIn={dropInMutation.mutate}
        onMark={(pid, status) => markAttendanceMutation.mutate({ personId: pid, status })}
        onDelete={(pid) => deleteAttendanceMutation.mutate(pid)}
        isPending={dropInMutation.isPending || markAttendanceMutation.isPending || deleteAttendanceMutation.isPending}
      />
    );
  }

  return (
    <RosterModule 
      roster={roster}
      attendance={attendance}
      onMark={(pid, status) => markAttendanceMutation.mutate({ personId: pid, status })}
      onMarkAllPresent={markAllPresentMutation.mutate}
      isPending={markAllPresentMutation.isPending || markAttendanceMutation.isPending}
    />
  );
};